import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DownloadIcon from "@mui/icons-material/Download";

/*
 * ResponsiveDraggableCollage
 *
 * Key Features:
 *   1. Responsive container that maintains a 16:9 aspect ratio (width: auto, height auto).
 *   2. Draggable divider lines stored as fractions (0..1) so resizing the window is supported.
 *   3. Rows/Cols up to 5, each (R-1) horizontal lines, (C-1) vertical lines.
 *   4. Images fill the cells in row-major order (some cells may remain empty if fewer images).
 *   5. Final collage is composited onto a canvas, then downloaded.
 *
 * Implementation Details:
 *   • The "previewRef" measures the container’s actual pixel width & height.
 *   • horizontalLines[i] is a fraction in [0,1], referencing the container’s height.
 *   • verticalLines[i] is fraction in [0,1], referencing the container’s width.
 *   • Dragging updates these fractions, with clamping to ensure lines don’t overlap too closely.
 *   • On "Generate Collage," we compute row boundaries & col boundaries in pixels and draw images.
 */

const ResponsiveDraggableCollage = () => {
  /* -----------------------------------
   * 1) Basic State
   * ----------------------------------- */
  const [images, setImages] = useState([]); // base64-encoded images
  const [rows, setRows] = useState(2);
  const [cols, setCols] = useState(2);
  const [collageDataUrl, setCollageDataUrl] = useState(null);

  // For 16:9 ratio, we let width be 100% (of parent), and height is automatically set.
  // We'll measure the actual rendered size to position lines in absolute coords.
  const previewRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  // --------------------------------------
  //  Divider Lines (as fractions)
  // --------------------------------------
  // For R rows => we have R-1 horizontal lines in [0..1].
  // Default for 2 rows => [0.5], meaning halfway down.
  const [horizontalLines, setHorizontalLines] = useState([0.5]);
  // For C cols => we have C-1 vertical lines in [0..1].
  // Default for 2 cols => [0.5], meaning halfway across.
  const [verticalLines, setVerticalLines] = useState([0.5]);

  // Track which line is being dragged
  // We'll store { type: 'horizontal' | 'vertical', index: number } or null
  const [dragLine, setDragLine] = useState(null);

  /* -----------------------------------
   * 2) Measure Container for Responsiveness
   * ----------------------------------- */
  useEffect(() => {
    const handleResize = () => {
      if (previewRef.current) {
        const rect = previewRef.current.getBoundingClientRect();
        setContainerWidth(rect.width);
        setContainerHeight(rect.height);
      }
    };
    handleResize(); // measure at mount

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* -----------------------------------
   * 3) Re-init lines on row/col changes
   * ----------------------------------- */
  useEffect(() => {
    // If only 1 row, no horizontal lines are needed
    if (rows < 2) {
      setHorizontalLines([]);
      return;
    }
    // Evenly distribute lines in [0..1]
    const step = 1 / rows;
    const newH = [];
    for (let i = 1; i < rows; i++) {
      newH.push(step * i);
    }
    setHorizontalLines(newH);
  }, [rows]);

  useEffect(() => {
    if (cols < 2) {
      setVerticalLines([]);
      return;
    }
    const step = 1 / cols;
    const newV = [];
    for (let i = 1; i < cols; i++) {
      newV.push(step * i);
    }
    setVerticalLines(newV);
  }, [cols]);

  /* -----------------------------------
   * 4) Image Upload
   * ----------------------------------- */
  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray = [];
    for (let i = 0; i < files.length; i++) {
      fileArray.push(files[i]);
    }
    // Convert each file to base64
    Promise.all(
      fileArray.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => resolve(event.target.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      )
    )
      .then((base64Images) => {
        setImages((prev) => [...prev, ...base64Images]);
      })
      .catch((err) => console.error(err));
  };

  /* -----------------------------------
   * 5) Handle Mousedown on Lines
   * ----------------------------------- */
  const onMouseDownHorizontal = (index, e) => {
    e.preventDefault();
    setDragLine({ type: "horizontal", index });
  };
  const onMouseDownVertical = (index, e) => {
    e.preventDefault();
    setDragLine({ type: "vertical", index });
  };

  /* -----------------------------------
   * 6) Mouse Move / Up Handlers
   * ----------------------------------- */
  const onMouseMove = (e) => {
    if (!dragLine || !previewRef.current) return;

    // We'll compute fraction = (mouseCoord - containerStartCoord) / containerDimension
    // Then clamp it, also ensure we don't cross neighboring lines by some minimal fraction
    const minGap = 0.02; // ~2% gap

    if (dragLine.type === "horizontal") {
      const { index } = dragLine;
      const rect = previewRef.current.getBoundingClientRect();
      const yPx = e.clientY - rect.top; // Y in px
      let newFrac = yPx / rect.height; // fraction in [0..1]
      if (newFrac < 0) newFrac = 0;
      if (newFrac > 1) newFrac = 1;

      // Sort lines, clamp relative to neighbors
      const sorted = [...horizontalLines].sort((a, b) => a - b);
      // line above is sorted[index-1] or 0
      // line below is sorted[index+1] or 1
      const lineAbove = index === 0 ? 0 : sorted[index - 1];
      const lineBelow = index === sorted.length - 1 ? 1 : sorted[index + 1];
      if (newFrac < lineAbove + minGap) newFrac = lineAbove + minGap;
      if (newFrac > lineBelow - minGap) newFrac = lineBelow - minGap;

      sorted[index] = newFrac;
      setHorizontalLines(sorted);
    } else if (dragLine.type === "vertical") {
      const { index } = dragLine;
      const rect = previewRef.current.getBoundingClientRect();
      const xPx = e.clientX - rect.left;
      let newFrac = xPx / rect.width;
      if (newFrac < 0) newFrac = 0;
      if (newFrac > 1) newFrac = 1;

      const sorted = [...verticalLines].sort((a, b) => a - b);
      const lineLeft = index === 0 ? 0 : sorted[index - 1];
      const lineRight = index === sorted.length - 1 ? 1 : sorted[index + 1];
      if (newFrac < lineLeft + minGap) newFrac = lineLeft + minGap;
      if (newFrac > lineRight - minGap) newFrac = lineRight - minGap;
      sorted[index] = newFrac;
      setVerticalLines(sorted);
    }
  };
  const onMouseUp = () => setDragLine(null);

  // Attach global listeners
  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragLine, horizontalLines, verticalLines]);

  /* -----------------------------------
   * 7) Generate Collage
   * ----------------------------------- */
  const createCollage = async () => {
    if (!images.length || !containerWidth || !containerHeight) return;

    // # of cells = rows * cols
    const totalCells = rows * cols;
    const usedImages = images.slice(0, totalCells);

    // Sort lines
    const sortedH = [...horizontalLines].sort((a, b) => a - b);
    const sortedV = [...verticalLines].sort((a, b) => a - b);

    // Row boundaries in fractions => [0, ...sortedH, 1]
    const rowFrac = [0, ...sortedH, 1];
    // Col boundaries => [0, ...sortedV, 1]
    const colFrac = [0, ...sortedV, 1];
    // Convert fractions to px
    // Canvas dimension = containerWidth x containerHeight
    // (You could scale up if you want higher-res downloads.)
    const canvasWidth = Math.floor(containerWidth);
    const canvasHeight = Math.floor(containerHeight);

    // Load images
    const loaded = await Promise.all(
      usedImages.map(
        (base64) =>
          new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = base64;
          })
      )
    );

    // Create a canvas
    const canvas = document.createElement("canvas");
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext("2d");

    // Fill white background
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Place each image
    let imgIndex = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (imgIndex < loaded.length) {
          const img = loaded[imgIndex];
          const x1 = colFrac[c] * canvasWidth;
          const x2 = colFrac[c + 1] * canvasWidth;
          const y1 = rowFrac[r] * canvasHeight;
          const y2 = rowFrac[r + 1] * canvasHeight;
          const cellW = x2 - x1;
          const cellH = y2 - y1;
          // Basic "cover" approach: just stretch to fill
          ctx.drawImage(img, x1, y1, cellW, cellH);
          imgIndex++;
        }
      }
    }

    // Convert to dataURL
    const finalUrl = canvas.toDataURL("image/jpeg", 0.9);
    setCollageDataUrl(finalUrl);
  };

  /* -----------------------------------
   * 8) Download & Reset
   * ----------------------------------- */
  const handleDownload = () => {
    if (!collageDataUrl) return;
    const link = document.createElement("a");
    link.href = collageDataUrl;
    link.download = "responsive-draggable-collage.jpg";
    link.click();
  };

  const handleReset = () => {
    setImages([]);
    setCollageDataUrl(null);
    setRows(2);
    setCols(2);
    setHorizontalLines([0.5]);
    setVerticalLines([0.5]);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          mb: 3,
          py: 2,
          textAlign: "center",
          backgroundColor: "#f2f2f2",
        }}
      >
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 1 }}>
          Collage Maker
        </Typography>
      </Box>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        {/* Upload */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            sx={{ borderRadius: 2, mr: 2 }}
          >
            Upload Images
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleFileChange}
            />
          </Button>
          <Typography variant="body2" color="text.secondary" display="inline">
            (Select as many as you like, up to rows×cols used)
          </Typography>
        </Box>

        {/* Thumbnails */}
        <Box sx={{ mb: 3, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {images.length ? (
            images.map((img, i) => (
              <Box
                key={i}
                component="img"
                src={img}
                alt={"thumb-" + i}
                sx={{
                  width: 60,
                  height: 60,
                  objectFit: "cover",
                  borderRadius: 1,
                  border: "1px solid #ccc",
                }}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No images yet
            </Typography>
          )}
        </Box>

        {/* Row & Col Select */}
        <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Rows</InputLabel>
            <Select
              label="Rows"
              value={rows}
              onChange={(e) => setRows(parseInt(e.target.value, 10))}
            >
              {[1, 2, 3, 4, 5].map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Columns</InputLabel>
            <Select
              label="Columns"
              value={cols}
              onChange={(e) => setCols(parseInt(e.target.value, 10))}
            >
              {[1, 2, 3, 4, 5].map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Responsive Container w/ 16:9 aspect ratio */}
        {/* We rely on CSS aspect-ratio for minimal code; older browsers may need fallback */}
        <Box
          ref={previewRef}
          sx={{
            position: "relative",
            width: "100%", // fill parent width
            maxWidth: 900, // optional max width
            aspectRatio: "16/9", // maintain 16:9 ratio
            border: "2px solid #ccc",
            borderRadius: 2,
            mb: 3,
            background:
              "repeating-conic-gradient(#eee 0% 25%, #fff 0% 50%) 50% / 20px 20px",
          }}
        >
          {/* Horizontal lines (draggable) */}
          {horizontalLines.map((frac, i) => {
            const topPos = frac * 100; // in %
            return (
              <Box
                key={`h-${i}`}
                sx={{
                  position: "absolute",
                  left: 0,
                  width: "100%",
                  height: "2px",
                  top: `${topPos}%`,
                  backgroundColor: "#33f",
                  cursor: "row-resize",
                  transform: "translateY(-1px)", // center line visually
                  zIndex: 10,
                }}
                onMouseDown={(e) => onMouseDownHorizontal(i, e)}
              />
            );
          })}
          {/* Vertical lines (draggable) */}
          {verticalLines.map((frac, i) => {
            const leftPos = frac * 100; // in %
            return (
              <Box
                key={`v-${i}`}
                sx={{
                  position: "absolute",
                  top: 0,
                  height: "100%",
                  width: "2px",
                  left: `${leftPos}%`,
                  backgroundColor: "#33f",
                  cursor: "col-resize",
                  transform: "translateX(-1px)",
                  zIndex: 10,
                }}
                onMouseDown={(e) => onMouseDownVertical(i, e)}
              />
            );
          })}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Button
            variant="contained"
            onClick={createCollage}
            disabled={!images.length}
            sx={{ borderRadius: 2 }}
          >
            Generate Collage
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleReset}
            startIcon={<RestartAltIcon />}
            sx={{ borderRadius: 2 }}
          >
            Reset
          </Button>
          {collageDataUrl && (
            <Button
              variant="contained"
              color="success"
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
              sx={{ borderRadius: 2 }}
            >
              Download
            </Button>
          )}
        </Box>

        {/* Final Collage Preview */}
        {collageDataUrl && (
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Collage Preview
              </Typography>
              <Box
                sx={{
                  border: "2px dashed #ccc",
                  borderRadius: 2,
                  p: 1,
                  textAlign: "center",
                  mx: "auto",
                  width: "100%",
                  overflow: "hidden",
                  maxWidth: 900,
                }}
              >
                <img
                  src={collageDataUrl}
                  alt="Collage preview"
                  style={{ maxWidth: "100%", borderRadius: 4 }}
                />
              </Box>
            </CardContent>
          </Card>
        )}
      </Paper>
    </Container>
  );
};

export default ResponsiveDraggableCollage;

/*
# Created/Modified files during execution:
ResponsiveDraggableCollage.jsx
*/
